import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuDownload, LuFileX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { EmptyState } from '../components/ProfileUi';

const FRAME = 'tw-block tw-h-[min(70vh,720px)] tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 dark:tw-border-white/10';

// docx-preview'in gri zemini yumuşatılır, dar ekranda sayfa kenar boşluğu küçülür
const SHADOW_CSS = `
  .docx-wrapper { background: #e2e8f0 !important; }
  @media (max-width: 639px) { .docx-wrapper { padding: 8px !important; } }
`;

/**
 * Word (.docx) belgesini sayfanın içinde çizer (docx-preview).
 *
 * Eskiden belge Office Online görüntüleyicisinde (view.officeapps.live.com)
 * açılıyordu; görüntüleyici Firebase Storage adreslerini açamadığı için Görev
 * Tanımları/Politikalardaki Word belgeleri boş kalıyordu. Dosya artık bucket'ın
 * CORS tanımıyla (cors.json) doğrudan indirilip tarayıcıda çiziliyor; harici
 * bir hizmete bağlı değil. Çizilemeyen dosyada (bozuk ya da eski .doc) indirme
 * önerilir.
 */
const WordDocumentView = ({ url, title, onReady, loadingLabel, errorLabel, downloadLabel, onDownload }) => {
  const containerRef = useRef(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    const render = async () => {
      try {
        // Kütüphane yalnızca Word belgesi açılınca yüklenir
        const [{ renderAsync }, response] = await Promise.all([import('docx-preview'), fetch(url)]);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const host = containerRef.current;
        if (cancelled || !host) return;
        // Shadow DOM: profildeki eski karanlık mod kuralları (body.dark-mode h6,
        // th, [class*="header"] ...) belgenin beyaz sayfalarındaki metne bulaşmasın
        const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
        root.innerHTML = `<style>${SHADOW_CSS}</style>`;
        const body = document.createElement('div');
        root.appendChild(body);
        await renderAsync(blob, body, root, {
          className: 'docx',
          inWrapper: true,
          ignoreLastRenderedPageBreak: true,
        });
        if (cancelled) return;
        setStatus('ready');
        onReadyRef.current?.();
      } catch (error) {
        console.error('Word belgesi görüntülenemedi:', error);
        if (!cancelled) setStatus('error');
      }
    };

    render();
    return () => { cancelled = true; };
  }, [url]);

  return (
    <>
      {status === 'loading' && (
        <div aria-busy="true" className={`${FRAME} tw-grid tw-animate-pulse tw-place-items-center tw-bg-slate-100 tw-text-sm tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400`}>
          {loadingLabel}
        </div>
      )}
      {status === 'error' && (
        <EmptyState
          compact
          icon={LuFileX}
          title={errorLabel}
          action={(
            <Button onClick={onDownload}>
              <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
              {downloadLabel}
            </Button>
          )}
        />
      )}
      {/* Belge yüklenirken de DOM'da kalır: çizim bu kutunun içine yapılır */}
      <div
        ref={containerRef}
        role="document"
        aria-label={title}
        className={status === 'ready' ? `${FRAME} tw-overflow-auto tw-bg-slate-200` : 'tw-hidden'}
      />
    </>
  );
};

WordDocumentView.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string,
  onReady: PropTypes.func,
  loadingLabel: PropTypes.string.isRequired,
  errorLabel: PropTypes.string.isRequired,
  downloadLabel: PropTypes.string.isRequired,
  onDownload: PropTypes.func.isRequired,
};

export default WordDocumentView;
